import { LifeIdDrawer } from '@components/ui';
import { useAppSelector } from '@hooks/app/redux';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import TStore from 'types/store';
import Badge from '../../../../components/badge';
import Button from '../../../../components/button';
import DrawerModal from '../../../../components/drawer-modal';
import { getDateFormat, getTimeFormat } from '../../../../helpers/utils-helpers';
import { memberApiV2 } from '../../../../lib/api';
import { actions } from '../../../../store/ducks/vital-history';
import { HistoryDrawerData, IDateRange } from '../../../../types/life-id';
import { DEFAULT_DATE_RANGE } from '../history-range-picker';
import LifeChart from '../life-chart';
import { HistoryDrawerSC, HistoryItemSC } from './history-drawer.styled';
import HistoryList from './history-list';

type TDrawerProps = {
  visible: boolean;
  onClose(): void;
  drawerData: HistoryDrawerData;
};

const News2History: React.FC = () => {
  const params = useParams<{ userId: string; jabberId: string }>();
  const [covidScoreList, setCovidScoreList] = useState<null | any[]>();
  const intl = useIntl();

  const providerId = useSelector((state: TStore) => state.users.business[0].id);

  useEffect(() => {
    // make axios call
    const getCovidScore = async (userId: string): Promise<void> => {
      const result = (await memberApiV2.getCovidScoreByDoctor(providerId, userId, 10)).data;
      // try to get body measure
      console.info('[HISTORY - NEWS2 WIDGET] - getting covid score list', result);
      const covidScoreList = result.data?.items?.length > 0 ? result.data.items : null;
      setCovidScoreList(covidScoreList);
    };
    getCovidScore(params.userId);
  }, [params.userId]);

  if (!covidScoreList) {
    return null;
  }

  return (
    <>
      <HistoryItemSC key={`${covidScoreList[0].datetime}_first`}>
        <div className="hi-content-col">
          <time>{moment(covidScoreList[0].datetime).format(getDateFormat())}</time>
          <div>{moment(covidScoreList[0].datetime).format(getTimeFormat())}</div>
          <div className="news2-more-info">
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_age' })}:</span>
              <span>{covidScoreList[0].age}</span>
            </div>
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_resp_rate' })}:</span>
              <span>{parseInt(covidScoreList[0].breathRate.breathRate, 10)}</span>
            </div>
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_supplemental_o2' })}:</span>
              <span>
                {covidScoreList[0].hasOxygenSupport
                  ? intl.formatMessage({ id: 'common.yes' })
                  : intl.formatMessage({ id: 'common.no' })}
              </span>
            </div>
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_oxygen_saturation' })}:</span>
              <span>{covidScoreList[0].oxygenSaturation.oxygenSaturation}%</span>
            </div>
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_body_temperature' })}:</span>
              <span>{covidScoreList[0].bodyTemperature.temperature}°C</span>
            </div>
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_blood_pressure' })}:</span>
              <span>{covidScoreList[0].bloodPressure.systolic}</span>
            </div>
            <div>
              <span>{intl.formatMessage({ id: 'life_id.news2_pulse' })}:</span>
              <span>{covidScoreList[0].heartRate.bpm}</span>
            </div>
          </div>
        </div>
        <div className="hi-value-col">
          {covidScoreList[0].score}
          &nbsp;
        </div>
        <div className="hi-type-col">
          <Badge text="life-id.manually" type="PRIMARY" />
        </div>
      </HistoryItemSC>
      {covidScoreList.slice(1).map((item, i) => (
        <HistoryItemSC key={`${item.datetime}_${i}`}>
          <div className="hi-content-col">
            <time>{moment(item.datetime).format(getDateFormat())}</time>
            <div>{moment(item.datetime).format(getTimeFormat())}</div>
          </div>
          <div className="hi-value-col">
            {item.score}
            &nbsp;
          </div>
          <div className="hi-type-col">
            <Badge text="life-id.manually" type="PRIMARY" />
          </div>
        </HistoryItemSC>
      ))}
    </>
  );
};

const News2Drawer: React.FC<TDrawerProps> = (props: TDrawerProps) => {
  const { visible, onClose } = props;
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);

  const intl = useIntl();

  return (
    <DrawerModal visible={visible} onClose={onClose} closeTheme="LIGHT">
      <HistoryDrawerSC>
        <div className="hd-header">
          <span style={{ color: 'white' }}>{intl.formatMessage({ id: 'life_id.news2_info' })}</span>
          &nbsp;
          {!moreInfoOpen && (
            <span className="news2__link-info" onClick={() => setMoreInfoOpen(true)}>
              {intl.formatMessage({ id: 'life_id.news2_more_info' })}
            </span>
          )}
          {moreInfoOpen && (
            <span className="news2__link-info" onClick={() => setMoreInfoOpen(false)}>
              {intl.formatMessage({ id: 'life_id.news2_less_info' })}
            </span>
          )}
        </div>
        <div className="hd-body">
          {moreInfoOpen && (
            <div className="news2__more-info-wrapper">
              <div className="news2__more-info-section">
                <span>{intl.formatMessage({ id: 'life_id.news2_age' })}</span>
                <ul>
                  <li>
                    <span>&lt; 65 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                  <li>
                    <span>&ge; 65</span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_resp_rate' })}</span>
                <ul>
                  <li>
                    <span>&le; 8</span>{' '}
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>
                  </li>
                  <li>
                    <span>9 - 11 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>12 - 20 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                  <li>
                    <span>21 - 24</span>{' '}
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +2</span>
                  </li>
                  <li>
                    <span>&ge; 25 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_supplemental_o2' })}</span>
                <ul>
                  <li>
                    <span>{intl.formatMessage({ id: 'common.yes' })}</span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +2</span>
                  </li>
                  <li>
                    <span>{intl.formatMessage({ id: 'common.no' })}</span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_oxygen_saturation' })}</span>
                <ul>
                  <li>
                    <span>&lt; 91 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3 </span>
                  </li>
                  <li>
                    <span>92-93% </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +2</span>
                  </li>
                  <li>
                    <span>94-95% </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>&ge; 96 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_body_temperature' })}</span>
                <ul>
                  <li>
                    <span>&lt; 35°C </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>{' '}
                  </li>
                  <li>
                    <span>35.1 - 36°C </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>36.1 - 38°C </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                  <li>
                    <span>38.1 - 39°C </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>&ge; 39.1°C </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +2</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_blood_pressure' })}</span>
                <ul>
                  <li>
                    <span>&lt; 90 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3 </span>
                  </li>
                  <li>
                    <span>91-100 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +2</span>
                  </li>
                  <li>
                    <span>101-110 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>111-219 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                  <li>
                    <span>&ge; 220 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_pulse' })}</span>
                <ul>
                  <li>
                    <span>&lt; 40 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3 </span>
                  </li>
                  <li>
                    <span>41-50</span>{' '}
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>51-90 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                  <li>
                    <span>91-110 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +1</span>
                  </li>
                  <li>
                    <span>111-130 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +2</span>
                  </li>
                  <li>
                    <span>131 </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>
                  </li>
                </ul>
                <span>{intl.formatMessage({ id: 'life_id.news2_consciousness' })}</span>
                <ul>
                  <li>
                    <span>{intl.formatMessage({ id: 'life_id.news2_alert' })}</span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: 0</span>
                  </li>
                  <li>
                    <span style={{ width: '200px' }}>
                      {intl.formatMessage({ id: 'life_id.news2_drowsiness' })}
                    </span>
                    <span>{intl.formatMessage({ id: 'life_id.news2_score' })}: +3</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
          {!moreInfoOpen && (
            <>
              <News2History />
              <div className="hd-body-bottom">
                <Button text="vital_history.back" isBlock onClick={onClose} />
              </div>
            </>
          )}
        </div>
      </HistoryDrawerSC>
    </DrawerModal>
  );
};

const HistoryDrawer: React.FC<TDrawerProps> = (props: TDrawerProps) => {
  const { visible, onClose, drawerData } = props;
  const type = props.drawerData.type;
  const dispatch = useDispatch();
  const meta = useAppSelector((store: TStore) => store.vitalHistory.meta);

  useEffect(() => {
    if (visible) {
      dispatch(actions.getHistoryMetaRequest(drawerData.type));
    }
  }, [drawerData.type, visible, dispatch]);

  const [dateRange, setDateRange] = useState<IDateRange>(DEFAULT_DATE_RANGE);
  const parentRef = React.useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  return (
    <LifeIdDrawer
      visible={visible}
      onClose={onClose}
      title={<FormattedMessage id={meta?.name ?? '-'} />}
      isInfiniteScroll
      ref={parentRef}
      destroyOnClose
    >
      <HistoryDrawerSC>
        <LifeChart
          // @ts-ignore
          recordType={type === 'SYSTOLIC' || type === 'DIASTOLIC' ? 'BLOOD_PRESSURE' : type}
          dateRange={dateRange}
          onRangeChange={setDateRange}
          onHeaderChange={setHeaderHeight}
        />
        {drawerData.args?.vitalListEntry ? (
          <HistoryList
            vitalListEntry={drawerData.args.vitalListEntry}
            range={dateRange}
            parentRef={parentRef}
            headerHeight={headerHeight}
          />
        ) : null}
      </HistoryDrawerSC>
    </LifeIdDrawer>
  );
};

const Drawer: React.FC<TDrawerProps> = (props: TDrawerProps) => {
  const { drawerData } = props;
  if (drawerData.type === 'NEWS2') {
    return <News2Drawer {...props} />;
  }
  return <HistoryDrawer {...props} />;
};

export default Drawer;
